import React, { forwardRef, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import { Box, InputBase, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * A one-time code entered one digit per box, the way sign-in and verification codes are entered
 * across current apps: typing moves to the next box, Backspace moves back, the arrow keys move
 * between boxes, and a pasted or autofilled code fills every box at once. Phones get the number
 * pad.
 *
 * `groups` splits the boxes the way the code is read out (a 7-digit share code is 3 + 4), with a
 * dash between groups. Controlled: `value` is the digits only, and `onComplete` fires once all of
 * them are in, so the caller can submit without a button press. The ref exposes focus().
 */
const CodeInput = forwardRef(({
  value = "",
  onChange,
  onComplete,
  groups = [3, 4],
  disabled = false,
  error = false,
  autoFocus = false,
  ariaLabel = "Code",
}, ref) => {
  const { t } = useTranslation("account");
  const length = groups.reduce((sum, n) => sum + n, 0);
  const digits = String(value || "").replace(/\D/g, "").slice(0, length);
  const refs = useRef([]);

  useImperativeHandle(ref, () => ({
    focus: () => setTimeout(() => refs.current[0]?.focus(), 0),
  }));

  const focusBox = (index) => {
    const box = refs.current[Math.max(0, Math.min(length - 1, index))];
    box?.focus();
    box?.select();
  };

  const commit = (next) => {
    const clean = next.replace(/\D/g, "").slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
    return clean;
  };

  // Typing a digit into box `index` (or pasting/autofilling several) writes from there on.
  const handleInput = (index, raw) => {
    const typed = raw.replace(/\D/g, "");
    if (!typed) return;
    const next = (digits.slice(0, index) + typed + digits.slice(index + typed.length)).slice(0, length);
    const clean = commit(next);
    focusBox(Math.min(index + typed.length, clean.length, length - 1));
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (digits[index]) {
        commit(digits.slice(0, index) + digits.slice(index + 1));
      } else if (index > 0) {
        commit(digits.slice(0, index - 1) + digits.slice(index));
        focusBox(index - 1);
      }
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    handleInput(index, event.clipboardData.getData("text"));
  };

  let offset = 0;
  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: { xs: 0.75, sm: 1 } }}
    >
      {groups.map((count, groupIndex) => {
        const start = offset;
        offset += count;
        return (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && (
              <Typography aria-hidden color="text.disabled" sx={{ px: 0.25 }}>
                –
              </Typography>
            )}
            {Array.from({ length: count }, (_, i) => {
              const index = start + i;
              return (
                <InputBase
                  key={index}
                  inputRef={(el) => {
                    refs.current[index] = el;
                  }}
                  value={digits[index] || ""}
                  onChange={(e) => handleInput(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={(e) => handlePaste(index, e)}
                  onFocus={(e) => e.target.select()}
                  disabled={disabled}
                  autoFocus={autoFocus && index === 0}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    // Lets the phone offer a code it received; only the first box takes it.
                    autoComplete: index === 0 ? "one-time-code" : "off",
                    "aria-label": t("common.digitOfTotal", { label: ariaLabel, number: index + 1, total: length }),
                    style: { textAlign: "center", padding: 0 },
                  }}
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 52, sm: 56 },
                    border: 1,
                    borderColor: error ? "error.main" : "divider",
                    borderRadius: 2,
                    typography: "h5",
                    transition: "border-color 120ms",
                    "&.Mui-focused": { borderColor: error ? "error.main" : "primary.main", borderWidth: 2 },
                  }}
                />
              );
            })}
          </React.Fragment>
        );
      })}
    </Box>
  );
});

CodeInput.displayName = "CodeInput";

CodeInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  groups: PropTypes.arrayOf(PropTypes.number),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  autoFocus: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default CodeInput;
